import { API_BASE } from '@/lib/api-base';

/**
 * Self-service signup — `/api/signup/*`.
 *
 * Unauthenticated by nature (the workspace does not exist yet), so these go
 * through plain `fetch`, never `authedFetch`: a 401 here is an answer for the
 * form, not the end of anybody's session.
 */

export interface SignupConfig {
  enabled: boolean;
  workspace_login: boolean;
  countries: { code: string; name: string }[];
  password_min_length: number;
  approval_required?: boolean;
  email_verification?: boolean;
  methods?: { password: boolean; google: boolean };
  password_unavailable_reason?: string | null;
  google_client_id?: string;
  captcha?: { provider: 'none' | 'turnstile'; site_key: string };
  terms_url?: string;
  privacy_url?: string;
  default_country?: string;
}

export interface SignupStep {
  key: string;
  label: string;
  status: 'PENDING' | 'RUNNING' | 'OK' | 'FAILED' | 'SKIPPED';
}

export type SignupStatusCode =
  | 'PENDING_VERIFICATION' | 'PENDING_APPROVAL' | 'PROVISIONING'
  | 'COMPLETED' | 'FAILED' | 'REJECTED' | 'EXPIRED';

export interface SignupStatus {
  signup_id: string;
  status: SignupStatusCode;
  method: 'PASSWORD' | 'GOOGLE';
  company_name: string;
  workspace_key: string;
  username: string;
  email: string;
  message: string;
  next_action: string;
  steps: SignupStep[];
  percent: number;
  login_path?: string;
  reason?: string;
  dev_verify_url?: string;
}

/** A refused request: the message is safe to show; `field` names the input. */
export class SignupApiError extends Error {
  constructor(public status: number, message: string, public field?: string, public code?: string) {
    super(message);
    this.name = 'SignupApiError';
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/signup${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new SignupApiError(0, 'Could not reach the server. Check your connection and try again.');
  }
  const body = (await res.json().catch(() => null)) as
    | { detail?: unknown }
    | null;
  if (!res.ok) {
    const d = body?.detail;
    if (typeof d === 'string') throw new SignupApiError(res.status, d);
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      const o = d as { message?: string; field?: string; code?: string };
      throw new SignupApiError(res.status, o.message ?? `HTTP ${res.status}`, o.field, o.code);
    }
    if (Array.isArray(d) && d.length) {
      const first = d[0] as { loc?: unknown[]; msg?: string };
      const field = Array.isArray(first.loc) ? String(first.loc[first.loc.length - 1]) : undefined;
      throw new SignupApiError(res.status, first.msg ?? 'Check the highlighted field.', field);
    }
    throw new SignupApiError(res.status, `The server answered HTTP ${res.status}.`);
  }
  return body as T;
}

export const signupApi = {
  config: () => call<SignupConfig>('/config'),
  available: (key: string) =>
    call<{ key: string; available: boolean; reason: string | null; suggestion: string | null }>(
      `/workspace-available?key=${encodeURIComponent(key)}`,
    ),
  suggest: (company: string) =>
    call<{ key: string }>(`/suggest-workspace?company=${encodeURIComponent(company)}`),
  withPassword: (body: Record<string, unknown>) =>
    call<SignupStatus>('', { method: 'POST', body: JSON.stringify(body) }),
  withGoogle: (body: Record<string, unknown>) =>
    call<SignupStatus>('/google', { method: 'POST', body: JSON.stringify(body) }),
  verify: (signupId: string, token: string) =>
    call<SignupStatus>('/verify', {
      method: 'POST', body: JSON.stringify({ signup_id: signupId, token }),
    }),
  resend: (signupId: string) =>
    call<SignupStatus>(`/${encodeURIComponent(signupId)}/resend`, { method: 'POST' }),
  status: (signupId: string) => call<SignupStatus>(`/${encodeURIComponent(signupId)}/status`),
};

/** The workspace ID the login page remembers between visits (per browser). */
export const WORKSPACE_STORAGE_KEY = 'ukn_workspace';

export function rememberedWorkspace(): string {
  try {
    return localStorage.getItem(WORKSPACE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function rememberWorkspace(key: string): void {
  try {
    if (key) localStorage.setItem(WORKSPACE_STORAGE_KEY, key);
    else localStorage.removeItem(WORKSPACE_STORAGE_KEY);
  } catch {
    /* private mode — the field is simply not pre-filled next time */
  }
}
